// Press handlers that tell a tap from a hold, for anything a thumb can press.
//
// A hold fires once, buzzes, and swallows the click that would otherwise
// follow it; a finger that moves is a scroll and fires nothing. Right-click is
// a hold for a pointer (and is what Android sends for a long-press too — the
// `fired` flag keeps it to one). Shared by the pads and the material shelf so
// a hold means the same thing everywhere in the room.

import { useEffect, useRef, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";

export const HOLD_MS = 450;
/** A finger that travels further than this is scrolling, not holding. */
export const HOLD_SLOP_PX = 10;

/** A short haptic tick where the platform offers one. */
export function buzz(ms = 8): void {
  try {
    navigator.vibrate?.(ms);
  } catch {
    // No haptics here.
  }
}

export interface HoldHandlers {
  onPointerDown: (e: ReactPointerEvent) => void;
  onPointerMove: (e: ReactPointerEvent) => void;
  onPointerUp: (e: ReactPointerEvent) => void;
  onPointerCancel: (e: ReactPointerEvent) => void;
  onPointerLeave: (e: ReactPointerEvent) => void;
  onContextMenu: (e: ReactMouseEvent) => void;
  /** True once per hold: the click after it is not a tap. */
  consumeHold: () => boolean;
}

export function useHold(onHold: (e: ReactPointerEvent | null) => void): HoldHandlers {
  const timer = useRef<number | undefined>(undefined);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const holdRef = useRef(onHold);
  holdRef.current = onHold;
  const cancel = (): void => {
    window.clearTimeout(timer.current);
    origin.current = null;
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return {
    onPointerDown: (e: ReactPointerEvent) => {
      if (e.button !== 0) return;
      fired.current = false;
      origin.current = { x: e.clientX, y: e.clientY };
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        fired.current = true;
        origin.current = null;
        buzz(12);
        holdRef.current(e);
      }, HOLD_MS);
    },
    onPointerMove: (e: ReactPointerEvent) => {
      const o = origin.current;
      if (o && Math.hypot(e.clientX - o.x, e.clientY - o.y) > HOLD_SLOP_PX) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onContextMenu: (e: ReactMouseEvent) => {
      e.preventDefault();
      cancel();
      if (fired.current) return;
      fired.current = true;
      holdRef.current(null);
    },
    consumeHold: (): boolean => {
      const was = fired.current;
      fired.current = false;
      return was;
    },
  };
}
